import { Component, OnDestroy, OnInit, computed, signal, viewChild, input, numberAttribute, effect } from '@angular/core';
import { ParametersService } from '@app/components/parameters/parameters.service';
import { Parameters } from '@app/components/parameters/parameters';
import { Router, ActivatedRoute, RouterOutlet } from '@angular/router';
import { MachineInfosService } from '@app/components/machine-infos/machine-infos.service';
import { MachineInfos } from '@app/components/machine-infos/machine-infos';
import { InputModalResult, NotificationService, PoeModalService, TranslationService } from '@app/services';
import { UserManagerService } from '@app/managers';
import { AddMachineDto } from '@app/dtos';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { PromptMachineNumberModalComponent } from '@app/components/prompt-machine-number-modal/prompt-machine-number-modal.component';
import { MachineData } from '@app/components/machine-infos/machinedata';
import { LoginDialogComponent } from '@app/components/common/login-dialog/login-dialog.component';
import { filter, first, map, Observable, of, switchMap } from 'rxjs';
import { ZMediaText } from '@app/components/machine-infos/z-media-text.class';
import { AuthService } from '@auth0/auth0-angular';
import { toSignal } from '@angular/core/rxjs-interop';
import { MachineService } from '@app/services/machine.service';
import { environment } from 'environments/environment';
import { NgClass } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { HighlightCardsComponent } from '@app/components/highlight-cards/highlight-cards.component';
import { FitmentsComponent } from '../fitments/fitments.component';
import { HighlightsAndDataComponent } from '../highlights-and-data/highlights-and-data.component';
import { ZmediasComponent } from '../zmedias/zmedias.component';
import { AuthPromptComponent } from '@app/components/auth-prompt/auth-prompt.component';
import { PlaygroundComponent } from '@app/components/playground/playground.component';
import { BreadcrumbsComponent } from '@app/components/breadcrumbs/breadcrumbs.component';
import { IonAccordion, IonAccordionGroup, IonContent, IonItem, IonLabel, IonList, IonPopover, IonSpinner, IonCard, IonCardContent, IonIcon } from '@ionic/angular/standalone';
import { distinct } from '@app/logic/functions';
import { MedienComponent } from '../medien/medien.component';
import { Crc32 } from '@aws-crypto/crc32';
import { addIcons } from 'ionicons';
import { informationCircleOutline } from 'ionicons/icons';


@Component({
    selector: 'app-machinepage',
    templateUrl: './machinepage.component.html',
    styleUrls: ['./machinepage.component.scss'],
    imports: [
        TranslateModule,
        NgClass,
        HighlightCardsComponent,
        FitmentsComponent,
        HighlightsAndDataComponent,
        ZmediasComponent,
        MedienComponent,
        AuthPromptComponent,
        PlaygroundComponent,
        BreadcrumbsComponent,
        IonAccordion,
        IonAccordionGroup,
        IonItem,
        IonLabel,
        IonSpinner,
        IonPopover,
        IonContent,
        IonList,
        RouterOutlet,
        IonCard,
        IonCardContent,
        IonIcon
    ]
})
export class MachinepageComponent implements OnInit, OnDestroy {
    readonly machineDocumentationAccordionGroup = viewChild<IonAccordion>('machineDocumentationAccordionGroup');
    readonly outerAccordionGroup = viewChild<IonAccordionGroup>('outerAccordionGroup');

    readonly openOperationManualEffect = effect(() => {
        if (this.machineDocumentationAccordionGroup() && this.outerAccordionGroup() && this.viewMode() === 'ba') {
            this.outerAccordionGroup().value = 'machinedocumentation';
            this.machineDocumentationAccordionGroup().value = 'operationmanual';
            this.scrollIntoView('operationmanual');
        }
    });

    embedded = input(false);
    readonly key = input(0, { transform: numberAttribute });
    readonly viewMode = signal<string>('');
    params: Parameters;
    machineinfos: MachineInfos;
    machineData: MachineData;
    fetching = true;

    fahrzeugNr: string;
    readonly backgrounds = signal<string[]>([]);
    readonly userMachinesResource = this.userManager.userMachinesResource;
    readonly userOwnsMachine = computed(() => {
        return this.machineDataSignal() && this.userMachinesResource.value()?.some(x => x.machineNumber === this.machineDataSignal().matnr);
    });

    readonly productGroupRestrictions = toSignal(this.parameterService.getParametersObservable().pipe(
        filter(params => !!params?.country),
        map(x => x.country.code),
        map(layer => environment.configuratorRestrictions.find(x => x.locale === layer)?.productGroups ?? [])), { initialValue: [] });

    readonly machineDataSignal = toSignal(this.machineInfosService.machineData$);
    readonly parameters = toSignal(this.parameterService.getParametersObservable());
    readonly declarationOfConformityHeadResource = this.machineService.declarationOfConformityHeadResource(() => {
        const machine = this.machineDataSignal();
        if (machine?.detectedType !== 'serial') {
            return undefined;
        }

        return machine.matnr;
    });

    readonly availableUpgrades = this.machineService.availableUpgrades(
        () => {
            const machine = this.machineDataSignal();
            if (machine?.detectedType !== 'serial') {
                return undefined;
            }
            return machine.equnr;
        },
        () => this.parameters()?.country?.code?.split('_')[1],
    );

    isAuthed = toSignal(this.authService.isAuthenticated$, { initialValue: false });
    constructor(
        private readonly parameterService: ParametersService,
        public readonly router: Router,
        private readonly machineInfosService: MachineInfosService,
        public readonly route: ActivatedRoute,
        private readonly translationService: TranslationService,
        private readonly userManager: UserManagerService,
        private readonly modalService: NgbModal,
        private readonly managedModalService: PoeModalService,
        private readonly authService: AuthService,
        private readonly notificationService: NotificationService,
        private readonly machineService: MachineService
    ) {
        addIcons({ informationCircleOutline });
    }

    onMouseMove(event: MouseEvent) {
        const popover = event.target as unknown as (IonPopover & HTMLElement);
        if (!popover.trigger) {
            return;
        }
        const trigger = document.getElementById(popover.trigger);
        if (!trigger) {
            return;
        }
        const rect = trigger.getBoundingClientRect();
        const insideTrigger =
            event.clientX >= rect.left - 10 &&
            event.clientX <= rect.right + 10 &&
            event.clientY >= rect.top - 10 &&
            event.clientY <= rect.bottom + 10;
        if (insideTrigger) {
            return;
        }
        const popoverViewport = popover.querySelector('.popover-viewport');
        if (!popoverViewport) {
            return;
        }
        const popoverRect = popoverViewport.getBoundingClientRect();
        const insidePopover =
            event.clientX >= popoverRect.left - 10 &&
            event.clientX <= popoverRect.right + 10 &&
            event.clientY >= popoverRect.top - 10 &&
            event.clientY <= popoverRect.bottom + 10;
        if (!insidePopover) {
            popover.dismiss();
        }
    }

    ngOnInit() {
        this.route.params.subscribe(params => {
            this.fahrzeugNr = params['machinekey'];
            if (this.fahrzeugNr) {
                this.parameterService.setFahrzeugnr(params['machinekey']);
                this.parameterService.nextRouteAfterLogin = this.router.url;
            }
        });

        this.route.queryParams.subscribe(params => {
            const viewModeFromParameters = params['viewmode'] ?? params['viewMode'];
            if (!viewModeFromParameters) {
                return;
            }
            if (viewModeFromParameters === 'ba') {
                const checksum = this.key();
                const calculatedChecksum = new Crc32().update(new TextEncoder().encode(this.fahrzeugNr)).digest();
                if (checksum !== calculatedChecksum) {
                    this.router.navigate(['/machine/' + this.fahrzeugNr]);
                    return;
                }
            }
            this.viewMode.set(viewModeFromParameters);
            if (this.viewMode() && this.viewMode() !== 'kiosk') {
                this.machineInfosService.viewMode = this.viewMode();
                this.router.navigate(['/machine/' + this.fahrzeugNr]);
            }
        });

        this.machineinfos = new MachineInfos();
        this.machineInfosService.isFetchRunning$.subscribe(result => {
            this.fetching = result;
        });

        this.machineInfosService.machineData$
            .pipe(filter(result => !!result))
            .subscribe(result => {
                this.machineinfos.maktx = result.maktx;
                this.machineinfos.consigned = result.consigned;
                this.machineinfos.detectedType = result.detectedType;
                this.machineinfos.matnr = result.matnr;
                this.machineinfos.serNr = result.sernr;
                this.machineinfos.equNr = result.equnr;
                this.machineinfos.chassisnr = result.chassisnr;
                this.machineData = result;

                if (this.machineData.machinephkey) {
                    this.backgrounds.update(prev => distinct([...prev, `https://www.poettinger.at/img/landtechnik/produkte/phkey/2000/${this.machineData.machinephkey}.jpg`]));
                } else if (this.machineData.phkey) {
                    this.backgrounds.update(prev => distinct([...prev, `https://www.poettinger.at/img/landtechnik/produkte/phkey/2000/${this.machineData.phkey}.jpg`]));
                } else {
                    this.backgrounds.update(prev => distinct([...prev, `https://www.poettinger.at/img/landtechnik/produkte/phkey/2000/LW.jpg`]));
                }
            });

        this.machineInfosService.fitmentsOptional$.subscribe(result => this.machineinfos.fitments.options = result);
        this.machineInfosService.fitmentsFitted$.subscribe(result => this.machineinfos.fitments.fitted = result);

        this.machineInfosService.videos$.subscribe(result => this.machineinfos.videos = result);
        this.machineInfosService.images$.subscribe(result => this.machineinfos.images = result);

        this.machineInfosService.specsheet$.subscribe(result => this.machineinfos.technicalData = result);

        this.machineInfosService.highlights$.subscribe(result => {
            this.machineinfos.highlights = result;
        });

        this.machineInfosService.brochures$.subscribe(result => this.machineinfos.brochures = result);
        this.machineInfosService.operationManuals$.subscribe(result => this.machineinfos.manuals = result);
        this.machineInfosService.retrofitManuals$.subscribe(result => this.machineinfos.retrofitManuals = result);
        this.machineInfosService.assemblingInstructions$.subscribe(result => this.machineinfos.assemblingInstructions = result);
        this.machineInfosService.repairingInstructions$.subscribe(result => this.machineinfos.repairingInstructions = result);
        this.machineInfosService.lubricationPlans$.subscribe(result => this.machineinfos.lubricationPlans = result);
        this.machineInfosService.sparepartLists$.subscribe(result => this.machineinfos.sparepartsLists = result);
        this.machineInfosService.mechatronicPlans$.subscribe(result => this.machineinfos.mechatronicPlans = result);
    }

    ngOnDestroy(): void {
        if (this.router.routerState.snapshot.url.includes('contact')) { // fill sessionStorage with machineNr for Contact-Autofill if next Page is '/contact'
            sessionStorage['machineNrCache'] = this.machineinfos.matnr;
            sessionStorage['serNrCache'] = this.machineinfos.serNr;
            sessionStorage['equNrCache'] = this.machineinfos.equNr;
            sessionStorage['maktxCache'] = this.machineinfos.maktx;
        }
    }

    showConfiguratorButton(rootId: string) {
        return this.productGroupRestrictions()?.includes(rootId);
    }

    areMultipleDocumentsAvailable(documents: ZMediaText[]) {
        return documents?.length > 1 || (documents?.length === 1 && documents[0]?.alternativeLanguages?.length > 1);
    }

    isSingleDocumentAvailable(documents: ZMediaText[]) {
        return documents?.length === 1 && documents[0]?.alternativeLanguages?.length === 1;
    }


    areDocumentsAvailable(documents: ZMediaText[]) {
        return documents?.length > 0;
    }

    openDeclarationOfConformity(): void {
        const machineNumber = this.machineinfos?.matnr ?? this.machineData?.matnr;
        if (!machineNumber) {
            return;
        }

        const url = `/${environment.apiPath}/machines/${encodeURIComponent(machineNumber)}/declaration-of-conformity`;
        window.open(url, '_blank');
    }

    //#region toggles
    openConfigurator(event: Event) {
        event.stopPropagation();
        this.router.navigate(['/configurator', 'new', this.fahrzeugNr], { queryParams: { viewMode: this.viewMode() === 'kiosk' ? 'kiosk' : null } });
    }

    accordionGroupChange = (event: CustomEvent) => {
        if (event.detail.value === 'fitments') {
            this.toogleFitmentsCards();
        }
        if (event.detail.value === 'sparepartlists') {
            this.toogleSparepartsLists();
        }
        this.scrollIntoView(event.detail.value);
    };

    async scrollIntoView(targetValue: string) {
        const accordions = document.getElementsByTagName('ion-accordion');
        const target = [...accordions].find(x => x.value === targetValue);
        if (target) {
            let attempts = 0; // wait for accordion to fully expand but give up after 1 second
            while (!target.classList.contains('accordion-expanded') && attempts++ < 20) {
                await new Promise(resolve => setTimeout(resolve, 50));
            }
            const targetRect = target.getBoundingClientRect();
            const isWithinViewport = targetRect.top >= 0 && targetRect.bottom <= window.innerHeight;
            if (!isWithinViewport) {
                const label = target.querySelector('ion-item[slot="header"]');
                label.scrollIntoView({ behavior: 'smooth' });
            }
        }
    }

    async toogleFitmentsCards() {
        if (this.machineinfos?.detectedType === 'serial') {
            return;
        }
        const chassisNr = await this.requestChassisNr(
            this.machineinfos,
            this.translationService.translateInstant('fitments'),
            this.translationService.translateInstant('addMachineAusstattung')
        );
        if (!chassisNr) {
            return;
        }

        this.router.navigate(['/machine', chassisNr]);
    }

    async toogleSparepartsLists() {
        if (this.machineinfos?.detectedType === 'serial') {
            return;
        }
        if (!this.isAuthed() || this.viewMode() === 'poetdoc') {
            return;
        }
        const chassisNr = await this.requestChassisNr(
            this.machineinfos,
            this.translationService.translateInstant('addMachineErsatzteilTitle'),
            this.translationService.translateInstant('addMachineErsatzteil')
        );
        if (!chassisNr) {
            return;
        }
        this.router.navigate(['/machine', chassisNr]);
    }

    private showTelemetryPinInputModal(): Observable<InputModalResult> {
        return this.managedModalService.showInputModal({
            title: this.translationService.translateInstant('takeOverMachineWithTelemetryUnit'),
            message: this.translationService.translateInstant('takeOverMachineWithTelemetryUnitMessage'),
            confirmText: this.translationService.translateInstant('ok'),
            cancelText: this.translationService.translateInstant('continueWithoutTelemetryUnit')
        }, {
            centered: true
        });
    }

    async onAddToMachinePark(machineInfo: MachineInfos) {
        if (!this.isAuthed()) {
            await this.requestLogin(this.translationService.translateInstant('LoginNeededAddMachine'));
        }

        this.parameterService.getParametersObservable().subscribe(par => {
            this.params = par;
        });
        if (machineInfo?.detectedType !== 'serial' && !await this.requestChassisNr(machineInfo)) {
            return;
        }
        if (this.params.fahrzeugNr && this.params.fahrzeugNr !== '') {
            this.router.navigate(['/machine', this.params.fahrzeugNr]);
        }
        if (this.userOwnsMachine()) {
            return;
        }

        this.machineService.getSuggestedTelemetryUnitId(this.machineData.equnr).pipe(
            first(),
            switchMap(suggestedTelemetryUnitId => {
                if (suggestedTelemetryUnitId) {
                    return this.showTelemetryPinInputModal().pipe(
                        map(modalResult => ({ suggestedTelemetryUnitId, pin: modalResult.value?.trim(), continueWithoutTelemetryUnit: modalResult.wasDismissed }))
                    );
                }
                return of({ suggestedTelemetryUnitId: undefined, pin: undefined, continueWithoutTelemetryUnit: true });
            }),
            switchMap(({ suggestedTelemetryUnitId, pin, continueWithoutTelemetryUnit }) => {
                const machine: AddMachineDto = {
                    name: this.machineData.maktx,
                    phkey: this.machineData.phkey,
                    machinephkey: this.machineData.machinephkey,
                    machineNumber: machineInfo.matnr,
                    sernr: machineInfo.serNr,
                    equnr: machineInfo.equNr,
                    telemetryUnitPin: pin,
                    telemetryUnitToConnect: continueWithoutTelemetryUnit ? undefined : suggestedTelemetryUnitId,
                    assignmentType: continueWithoutTelemetryUnit ? 'Viewer' : 'Owner'
                };
                return this.userManager.addUserMachine(machine);
            })
        ).subscribe({
            next: () => {
                this.notificationService.notifySavedSuccessfully();
                this.router.navigate(['/myMachines/']);
            },
            error: e => {
                if (e.status === 409) {
                    this.notificationService.notifyWarningWithTranslate('Error', 'machineAlreadyInPark');
                    return;
                }
                this.notificationService.notifyErrorWithTranslate('Error', 'saveMachineError');
            }
        });
    }

    private fetchMachineData(machineInfo: MachineInfos): void {
        this.machineInfosService.fetchMachineData(machineInfo.matnr, 'de_AT').subscribe(data => {
            this.machineData = data;
            this.backgrounds.set([`https://www.poettinger.at/img/landtechnik/produkte/phkey/2000/${this.machineData?.machinephkey}.jpg`]);
        });
    }

    private async requestChassisNr(
        machineInfo: MachineInfos,
        modalTitle: string = this.translationService.translateInstant('addMachine'),
        modalBody: string = this.translationService.translateInstant('addMachineNeedChassisNr')
    ): Promise<string | null> {
        try {
            const modalref = this.modalService.open(PromptMachineNumberModalComponent, { centered: true });
            if (modalTitle) {
                modalref.componentInstance.title = modalTitle;
            }
            if (modalBody) {
                modalref.componentInstance.bodyText = modalBody;
            }
            const machineNr = await modalref.result;
            if (machineNr === '') {
                return null;
            }
            machineInfo.matnr = machineNr;
            machineInfo.detectedType = undefined;
            this.fetchMachineData(machineInfo);
            return machineNr;
        } catch { // if modal is dismissed, catch block is entered
            return null;
        }
    }

    private requestLogin(modalBody?: string): Promise<boolean> {
        const modalref = this.modalService.open(LoginDialogComponent, { size: 'md', centered: true });
        if (modalBody) {
            modalref.componentInstance.bodyText = modalBody;
        }
        return modalref.result;
    }
}
